import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Box,
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
} from '@chakra-ui/react';
import { InputField } from 'app/components/Core';
import {
  PreviewClickProps,
  ShareLinkList,
} from 'app/components/Shares/ShareLinkList';
import { SharedLink } from 'types/shares.types';
import { Link } from 'types/api.types';
import * as yup from 'yup';
import { dateFormats, fieldValidationMessages } from 'app/constants';
import { format } from 'date-fns';
import { AppState } from 'app/store';
import { useIsMounted } from 'app/utils/hooks';
import { UpdateSharedLinkEffectData } from 'app/store/Shares/effects.types';
import { GetSharedListEffectProps } from '../types';
import { titleSx } from './styles';

interface ShareLinkDialogProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  guid: string;
  addLink: (title: string) => Promise<SharedLink>;
  revokeLink: (linkGuid: string, link: Link) => void;
  sharedLinks: SharedLink[];
  isLoading: boolean;
  playlist?: boolean;
  updateSharedLink: (
    link: Link,
    data: UpdateSharedLinkEffectData['data'],
    linkGuid?: string
  ) => Promise<SharedLink | void>;
  getSharedListEffect: GetSharedListEffectProps;
  getSharedLinkListNextLink: (state: AppState) => Link | undefined;
  onPreviewClick: (_: PreviewClickProps) => void;
}

interface FormProps {
  linkTitle: string;
}

const formValidation = yup.object().shape({
  linkTitle: yup
    .string()
    .max(40, ({ max }) => fieldValidationMessages.MAX_CHAR(max)),
});

const defaultLinkTitle = `Link ${format(new Date(), dateFormats.LIST_FORMAT)}`;

export const ShareLinkDialog: React.FC<ShareLinkDialogProps> = ({
  isOpen,
  title,
  onClose,
  addLink,
  revokeLink,
  sharedLinks,
  isLoading,
  updateSharedLink,
  guid,
  getSharedListEffect,
  getSharedLinkListNextLink,
  onPreviewClick,
  playlist = false,
}) => {
  const isMounted = useIsMounted();
  const {
    handleSubmit,
    reset,
    register,
    formState: { isValid, errors },
  } = useForm<FormProps>({
    defaultValues: {
      linkTitle: '',
    },
    mode: 'onChange',
    resolver: yupResolver(formValidation),
  });

  const [isCreating, setIsCreating] = useState(false);

  const onSubmit = async (data: FormProps) => {
    setIsCreating(true);
    try {
      await addLink(
        data.linkTitle.trim().length > 0 ? data.linkTitle : defaultLinkTitle
      );
    } catch (e) {
      // proceed
    } finally {
      if (isMounted()) {
        reset();
        setIsCreating(false);
      }
    }
  };

  useEffect(() => {
    if (!isOpen) {
      reset();
    }
  }, [reset, isOpen]);

  const isEmptyList = !isLoading && !sharedLinks?.length;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader data-testid="dialog-title" title="Share">
          Share {playlist ? 'Playlist' : 'Audio'}
          <Text tabIndex={-1} sx={titleSx} title={title}>
            {title}
          </Text>
        </ModalHeader>
        <ModalBody
          data-testid="dialog-content"
          maxHeight="400px"
          minHeight="200px"
        >
          <form onSubmit={handleSubmit(onSubmit)}>
            <Box display="flex" justifyContent="space-between" maxW="400px">
              <InputField
                label="Name"
                data-testid="create-link-input"
                placeholder={defaultLinkTitle}
                {...register('linkTitle')}
                isInvalid={Boolean(errors.linkTitle)}
                errorMessage={errors.linkTitle?.message}
              />
              <Button
                ml={4}
                type="submit"
                isDisabled={isCreating || !isValid}
                flexShrink={0}
              >
                Create Link
              </Button>
            </Box>
          </form>
          {isEmptyList && (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              pt={6}
            >
              <Text fontSize="md" fontWeight={500}>
                No links created yet
              </Text>
              <Text fontSize="sm" color="textSubdued">
                Create a link above to get started
              </Text>
            </Box>
          )}
          {!isEmptyList && (
            <ShareLinkList
              sharedLinks={sharedLinks}
              updateSharedLink={updateSharedLink}
              revokeLink={revokeLink}
              loading={isLoading}
              guid={guid}
              getSharedListEffect={getSharedListEffect}
              getSharedLinkListNextLink={getSharedLinkListNextLink}
              onPreviewClick={onPreviewClick}
            />
          )}
        </ModalBody>
        <ModalFooter data-testid="dialog-actions">
          <Button data-testid="close-share-dialog" onClick={onClose}>
            Done
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
